import {
  IConformationBreedResult,
  IConformationClassResult,
  IConformationRegularBreedAwards,
  IConformationShowResults,
  IScore,
  IShow,
} from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { padPlacements } from "./labels";
import { ConformationStream, LicensedStream } from "./types";

const LEVEL = Constants.dog_class_element_level;
const ELEMENT = Constants.dog_class_element;

export function conformationOf(show: IShow): IConformationShowResults | undefined {
  return show.conformation_results;
}

/**
 * Saved breed row for a stream. v1 rows have no `stream`: they are Regular, and any
 * Altered class placements stored on them are split out as a synthetic Altered row.
 */
export function findStreamBreed(
  show: IShow,
  breed: string,
  breedGroup: string,
  stream: LicensedStream
): IConformationBreedResult | undefined {
  const rows = conformationOf(show)?.breed_results || [];
  const exact = rows.find(
    (row) => row.breed === breed && row.breed_group === breedGroup && row.stream === stream
  );
  if (exact) return exact;

  const legacy = rows.find(
    (row) => row.breed === breed && row.breed_group === breedGroup && !row.stream
  );
  if (!legacy) return undefined;

  if (stream === "regular") return legacy;

  const alteredClasses = (legacy.class_results || []).filter(
    (row) => row.show_element === ELEMENT.conformation_altered
  );
  if (!alteredClasses.length) return undefined;
  return {
    breed,
    breed_group: breedGroup,
    stream: "altered",
    class_results: alteredClasses,
  };
}

/** Awards for a breed row; maps v1 `winners_*` onto Best Male / Best Female when asked. */
export function resolveAwards(
  breed: IConformationBreedResult | undefined,
  useLegacyWinners: boolean
): IConformationRegularBreedAwards {
  if (!breed) return {};
  if (breed.awards) return breed.awards;
  if (!useLegacyWinners) return {};

  const champion = (breed.class_results || []).find(
    (row) => row.level === LEVEL.champion && !row.sex && !row.variety
  );
  const grand = (breed.class_results || []).find(
    (row) => row.level === LEVEL.grand_champion && !row.sex && !row.variety
  );

  return {
    best_male: breed.winners_male,
    reserve_best_male: breed.reserve_winners_male,
    best_female: breed.winners_female,
    reserve_best_female: breed.reserve_winners_female,
    best_of_breed: breed.best_of_breed,
    champion: champion?.placements?.[0],
    reserve_champion: champion?.placements?.[1],
    grand_champion: grand?.placements?.[0],
    reserve_grand_champion: grand?.placements?.[1],
  };
}

export function findClassPlacements(
  rows: IConformationClassResult[] | undefined,
  level: string,
  sex?: string,
  variety?: string
): string[] {
  const match = (rows || []).find(
    (row) =>
      row.level === level &&
      (row.sex || "") === (sex || "") &&
      (row.variety || "") === (variety || "")
  );
  return padPlacements(match?.placements);
}

export function seedBrace(
  show: IShow,
  breed: string,
  breedGroup: string
): { placements: string[]; bestBrace: string } {
  const saved = conformationOf(show)?.non_licensed_breed_results?.find(
    (row) => row.breed === breed && row.breed_group === breedGroup && row.level === "brace"
  );
  if (saved) {
    return { placements: padPlacements(saved.placements), bestBrace: saved.best_of_breed || "" };
  }

  const legacy = (conformationOf(show)?.breed_results || []).find(
    (row) => row.breed === breed && row.breed_group === breedGroup
  );
  const classResult = legacy?.non_licensed_results?.find(
    (row) => row.level === LEVEL.brace || row.show_element === ELEMENT.brace
  );
  return { placements: padPlacements(classResult?.placements), bestBrace: "" };
}

export function readStreamShow(
  show: IShow,
  stream: ConformationStream
): { best_in_show: string; reserve_best_in_show: string } {
  const awards = conformationOf(show)?.show_results;
  const nested = awards?.[stream];
  if (stream === "regular") {
    return {
      best_in_show: nested?.best_in_show || awards?.best_in_show || "",
      reserve_best_in_show: nested?.reserve_best_in_show || "",
    };
  }
  if (stream === "altered") {
    return {
      best_in_show: nested?.best_in_show || awards?.best_altered_in_show || "",
      reserve_best_in_show: nested?.reserve_best_in_show || "",
    };
  }
  return {
    best_in_show: nested?.best_in_show || "",
    reserve_best_in_show: nested?.reserve_best_in_show || "",
  };
}

export function seedNonLicensed(
  show: IShow,
  breed: string,
  breedGroup: string,
  level: "novice_puppy" | "veteran",
  scores: IScore[]
): { male: string[]; female: string[]; bestOfBreed: string } {
  const saved = conformationOf(show)?.non_licensed_breed_results?.find(
    (row) => row.breed === breed && row.breed_group === breedGroup && row.level === level
  );
  if (saved) {
    return {
      male: padPlacements(saved.male_placements),
      female: padPlacements(saved.female_placements),
      bestOfBreed: saved.best_of_breed || "",
    };
  }

  const levelName = level === "veteran" ? LEVEL.veteran : LEVEL.novice_puppy;
  const legacy = (conformationOf(show)?.breed_results || []).find(
    (row) => row.breed === breed && row.breed_group === breedGroup
  );
  const classResult = legacy?.non_licensed_results?.find(
    (row) => row.level === levelName || row.show_element === levelName
  );
  const male: string[] = [];
  const female: string[] = [];
  (classResult?.placements || []).forEach((id) => {
    if (!id) return;
    const score = scores.find((row) => row._id === id);
    if (score?.dog_sex === "Female") female.push(id);
    else male.push(id);
  });
  return {
    male: padPlacements(male),
    female: padPlacements(female),
    bestOfBreed: "",
  };
}

/** Saved Regular group placements for a breed group. */
export function readGroupPlacements(show: IShow, breedGroup: string): string[] {
  const saved = conformationOf(show)?.group_results?.find(
    (row) => row.breed_group === breedGroup && (row.stream || "regular") === "regular"
  );
  return padPlacements(saved?.placements);
}

/** Saved Best of Breed for a licensed stream + breed, honouring legacy fields. */
export function savedBestOfBreed(
  show: IShow,
  breed: string,
  breedGroup: string,
  stream: LicensedStream
): string | undefined {
  const row = findStreamBreed(show, breed, breedGroup, stream);
  if (!row) return undefined;
  return row.awards?.best_of_breed || row.best_of_breed || undefined;
}

/** Saved Best of Breed / Best Brace for a non-licensed level + breed. */
export function savedNonLicensedBestOfBreed(
  show: IShow,
  breed: string,
  breedGroup: string,
  level: "novice_puppy" | "veteran" | "brace"
): string | undefined {
  return (
    conformationOf(show)?.non_licensed_breed_results?.find(
      (row) => row.breed === breed && row.breed_group === breedGroup && row.level === level
    )?.best_of_breed || undefined
  );
}
